/**
 * Auth compatibility utilities that remain in utils/ because they deal with
 * browser navigation/redirects rather than core token management.
 *
 * Token logic lives in @/lib/auth — this file only re-exports and adds
 * redirect/navigation helpers.
 */

import { hasEmbedUrlSession } from '@/lib/auth/embedUrlSession';
import { clearMagicToken, getLiveEmbedUrlSessionToken, getMagicToken } from '@/lib/auth/magicToken';
import { isTokenExpired } from '@/lib/auth/tokenDecode';
import { getAccessToken, isEmbedMode, removeAccessToken } from '@/lib/auth/tokenStorage';
import { getHubBrand } from '@/store';

const REDIRECT_AFTER_LOGIN_KEY = 'redirectAfterLogin';
const LAST_REDIRECT_KEY = 'lastLoginRedirect';
const REDIRECT_COOLDOWN_MS = 5000;

/**
 * Get any valid auth token (Auth0 or magic) — sync, no network.
 * @returns {{ token: string, source: 'auth0' | 'magic' } | null}
 */
export const getValidAuthToken = () => {
  if (typeof window === 'undefined') return null;

  if (hasEmbedUrlSession() && isEmbedMode()) {
    const urlSessionToken = getLiveEmbedUrlSessionToken();
    return urlSessionToken ? { token: urlSessionToken, source: 'magic' } : null;
  }

  const accessToken = getAccessToken();
  if (accessToken) {
    if (!isTokenExpired(accessToken)) {
      return { token: accessToken, source: 'auth0' };
    }
    // Expired — clean up so isAuthenticated reflects reality
    removeAccessToken();
  }

  const magicToken = getMagicToken();
  if (magicToken) {
    return { token: magicToken.token, source: 'magic' };
  }

  return null;
};

/**
 * Get stored user info from localStorage.
 * @returns {{ userID: string, brandID: string | null } | null}
 */
export const getStoredUserInfo = () => {
  if (typeof window === 'undefined') return null;

  const userID = localStorage.getItem('userID');
  const brandID = getHubBrand().brandID ?? null;

  if (!userID) return null;

  return { userID: userID.trim(), brandID: brandID || null };
};

/**
 * Redirect to login, saving current URL for return after login.
 * Guards against redirect loops.
 * @returns {boolean} whether a redirect was actually triggered (false = suppressed by a guard)
 */
export const loginWithRedirect = ({ returnPath, brandUuid } = {}) => {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname === '/') return false;

  const lastRedirect = Number(sessionStorage.getItem(LAST_REDIRECT_KEY) || 0);
  if (Date.now() - lastRedirect < REDIRECT_COOLDOWN_MS) return false;

  sessionStorage.setItem(LAST_REDIRECT_KEY, String(Date.now()));
  const returnUrl = new URL(returnPath || window.location.href, window.location.origin);
  const loginUrl = new URL('/', window.location.origin);
  if (brandUuid) {
    returnUrl.searchParams.set('brandUuid', brandUuid);
    loginUrl.searchParams.set('brandUuid', brandUuid);
  }
  localStorage.setItem(REDIRECT_AFTER_LOGIN_KEY, returnUrl.href);
  window.location.href = brandUuid ? loginUrl.href : window.location.origin;
  return true;
};

/**
 * Get and clear the redirect URL (call after login).
 * @returns {string | null}
 */
export const getAndClearRedirectUrl = () => {
  if (typeof window === 'undefined') return null;
  const url = localStorage.getItem(REDIRECT_AFTER_LOGIN_KEY);
  if (url) localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
  return url;
};

/**
 * Check if user is authenticated and redirect to login if not.
 * @returns {{ userID: string, brandID: string } | null}
 */
export const requireAuth = () => {
  const userInfo = getStoredUserInfo();
  if (!userInfo?.userID) {
    loginWithRedirect();
    return null;
  }
  return userInfo;
};
